import { Button } from '@src/design-system/button';

import type { ColumnDef } from '@tanstack/react-table';

import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@src/design-system/dialog';
import { formatDate } from '@shared/utils/helpers/format-date';

import { EditMessageIcon } from '@shared/assets/icons/shared/edit-message-icon';
import type { TLeadNote } from '@src/shared/types/lead-note';
import type { TMemberMini } from '@src/shared/types/member';
import { AppointmentNotesModal } from '@features/participants/pages/detail/components/participant-tabs/appointments-tab/modals/appointment-notes-modal';
import { CreateAppointmentModal } from '@features/leads/pages/detail/tabs/appointments-tab/modals/create-appointment-modal';
import { createAlphabeticalSortingFunction } from '@src/design-system/molecules/ticlick-table/table-cells/utils/sorting';

import { LeadNameCell } from '@src/features/leads/pages/list/lead-table/cells/lead-name-cell';
import { LeadNotesCell } from '@features/leads/pages/list/lead-table/cells/lead-notes-cell';
import { LeadStatusCell } from '@src/features/leads/pages/list/lead-table/cells/lead-status-cell';
import { LeadRankCell } from '@src/features/leads/pages/list/lead-table/cells/lead-rank-cell';
import { LeadPhoneNumberCell } from '@features/leads/pages/list/lead-table/cells/lead-phone-number-cell';
import { LeadEmailCell } from '@features/leads/pages/list/lead-table/cells/lead-email-cell';
import { LeadInterestedProgramsCell } from '@features/leads/pages/list/lead-table/cells/lead-interested-programs-cell';
import { LeadAssigneeListCell } from '../../list/lead-table/cells/lead-assignee-list-cell';
import { LabelAction } from '../../list/actions/label-action';
import { FollowUpChannelCell } from './cells/follow-up-channel-cell';
import { FollowUpStatusCell } from './cells/follow-up-status-cell';

import { LEAD_NOTE_STATUS } from '@src/shared/types/lead-note';
import type { ValueOf } from '@src/shared/types/utilities';
import { membersFilterConfig } from '@shared/features/views/configs/member-filter-config';
import { programFilterConfig } from '@shared/features/views/configs/program-filter-config';
import { leadChannelFilterConfig } from '@shared/features/views/configs/lead-channel-filter-config';
import { leadScreeningMeetingDateFilterConfig } from '@shared/features/views/configs/lead-screening-meeting-date-filter-config';
import { labelFilterConfig } from '@shared/features/views/configs/label-filter-config';
import { leadRankFilterConfig } from '@shared/features/views/configs/lead-rank-filter-config';
import { leadStatusFilterConfig } from '@shared/features/views/configs/lead-status-filter-config';

const formatName = (member?: TMemberMini) =>
  member ? `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim() : '';

export const staticColumns: ColumnDef<TLeadNote>[] = [
  {
    id: 'name',
    accessorFn: (row) => row.lead.name,
    header: 'Name',
    sortingFn: createAlphabeticalSortingFunction('lead.name'),
    cell: ({ row }) => <LeadNameCell lead={row.original.lead} />,
  },
  {
    id: 'email',
    accessorFn: (row) => row.lead.email,
    header: 'Email',
    sortingFn: createAlphabeticalSortingFunction('lead.email'),
    cell: ({ row }) => <LeadEmailCell lead={row.original.lead} />,
  },

  {
    id: 'phone_number',
    accessorFn: (row) => row.lead.phone_number,
    header: 'Phone Number',
    sortingFn: createAlphabeticalSortingFunction('lead.phone_number'),
    cell: ({ row }) => <LeadPhoneNumberCell lead={row.original.lead} />,
  },
  {
    id: 'assignee',
    accessorFn: (row) => row.lead.assignee,
    sortingFn: createAlphabeticalSortingFunction('lead.assignee.first_name'),
    header: 'Assignee to',
    meta: {
      columnFilter: membersFilterConfig({ facetName: 'assignee' }),
    },
    cell: ({ row }) => {
      return <LeadAssigneeListCell lead={row.original.lead} />;
    },
  },
  {
    id: 'programs',
    accessorFn: (row) => row.lead.programs,
    header: 'Program',
    meta: {
      columnFilter: programFilterConfig,
    },
    cell: (props) => {
      const { row } = props;
      return <LeadInterestedProgramsCell lead={row.original.lead} />;
    },
  },
  {
    id: 'meetings_screening_interview',
    accessorFn: (row) => row.lead.screening_meeting_date,
    header: 'Screening Interviews',
    sortingFn: createAlphabeticalSortingFunction('lead.screening_meeting_date'),
    meta: { columnFilter: leadScreeningMeetingDateFilterConfig },
    cell: ({ getValue, row }) => {
      const meeting = getValue() as string;
      if (!meeting)
        return (
          <Dialog>
            <DialogTrigger>
              <Button
                size='iconSm'
                variant='primaryOutline'
                className='border-gray-200'
              >
                +
              </Button>
            </DialogTrigger>
            <DialogContent>
              <CreateAppointmentModal lead_id={row.original.id} />
            </DialogContent>
          </Dialog>
        );

      return (
        <span className='capitalize'>
          <span className='block'>{formatDate(meeting)}</span>
        </span>
      );
    },
  },
  {
    id: 'screening_interview_notes',
    accessorFn: (row) => row.lead.screening_meeting_id,
    sortingFn: createAlphabeticalSortingFunction('lead.screening_meeting_id'),
    header: 'Meetings Note',
    cell: ({ row }) => {
      const meetingId = row.original.lead.screening_meeting_id;
      const disabled = !meetingId;
      return (
        <Dialog>
          <DialogTrigger disabled={disabled}>
            <Button
              size='iconSm'
              variant='primaryOutline'
              className={`border-gray-200 ${!row.original.lead.has_meeting_notes ? 'opacity-50' : ''}`}
              disabled={disabled}
            >
              <EditMessageIcon />
            </Button>
          </DialogTrigger>

          <DialogContent>
            <AppointmentNotesModal id={meetingId} />
          </DialogContent>
        </Dialog>
      );
    },
  },
  {
    id: 'lead_notes',
    accessorFn: (row) => row.lead,
    header: 'Lead Notes',
    cell: ({ row }) => (
      <LeadNotesCell
        nextFollowUpDate={row.original.next_follow_up_date}
        lead={row.original.lead}
      />
    ),
  },
  {
    id: 'rank',
    accessorFn: (row) => row.lead.rank,
    sortingFn: createAlphabeticalSortingFunction('lead.rank'),
    meta: {
      columnFilter: leadRankFilterConfig,
    },
    header: 'Ranks',
    cell: ({ row }) => {
      return (
        <LeadRankCell
          leadId={row.original.lead.id}
          rank={row.original.lead.rank}
        />
      );
    },
  },
  {
    id: 'status',
    accessorFn: (row) => row.lead.status,
    sortingFn: createAlphabeticalSortingFunction('lead.status.name'),
    header: 'Lead Status',
    meta: {
      columnFilter: leadStatusFilterConfig,
    },
    cell: ({ row }) => {
      return (
        <LeadStatusCell
          leadId={row.original.lead.id}
          status={row.original.lead.status}
        />
      );
    },
  },
  {
    id: 'channel',
    accessorKey: 'channel',
    header: 'Channel',
    sortingFn: createAlphabeticalSortingFunction('channel'),
    meta: {
      columnFilter: leadChannelFilterConfig,
    },
    cell: ({ row }) => (
      <FollowUpChannelCell
        note={row.original}
        channel={row.original.channel}
      />
    ),
  },
  {
    id: 'resolved_by',
    accessorKey: 'resolved_by',
    sortingFn: createAlphabeticalSortingFunction('resolved_by'),
    header: 'Resolved By',
    cell: ({ row }) => {
      const { resolved_by, resolved_by_partner } = row.original;
      const name = resolved_by
        ? formatName(resolved_by)
        : formatName(resolved_by_partner);

      return <span>{name || '—'}</span>;
    },
  },
  {
    id: 'resolved_at',
    accessorKey: 'resolved_at',
    sortingFn: createAlphabeticalSortingFunction('resolved_at'),
    header: 'Resolved At',
    cell: ({ row }) => {
      const resolvedAt = row.original.resolved_at;
      return <span>{resolvedAt ? formatDate(resolvedAt) : '—'}</span>;
    },
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Follow Up Status',
    sortingFn: createAlphabeticalSortingFunction('status'),
    cell: ({ row }) => (
      <FollowUpStatusCell
        note={row.original}
        status={
          (row.original.status ?? LEAD_NOTE_STATUS.today) as ValueOf<
            typeof LEAD_NOTE_STATUS
          >
        }
      />
    ),
  },
  {
    id: 'labels',
    accessorFn: (row) => row.lead.labels,
    header: ' Labels',
    meta: {
      columnFilter: labelFilterConfig,
    },
    cell: (props) => {
      const { row, getValue } = props;
      return (
        <LabelAction
          labels={(getValue() as []) ?? []}
          id={row.original.lead.id}
        />
      );
    },
  },
  {
    id: 'updated_at',
    accessorKey: 'updated_at',
    sortingFn: createAlphabeticalSortingFunction('updated_at'),
    header: 'Last Update',
    cell: (props) => {
      const { getValue } = props;
      const date = getValue() as string;
      return (
        <span>
          {new Date(date).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      );
    },
  },
];
